import React, { useState } from 'react';
import { reportsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { FileText, Download, Loader2, Users, Calendar, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';

const Reports = () => {
  const { canEdit } = useAuth();
  const [downloading, setDownloading] = useState({});

  const handleDownload = async (type, format) => {
    const key = `${type}-${format}`;
    setDownloading({ ...downloading, [key]: true });
    
    try {
      let response;
      let filename;
      
      if (type === 'students' && format === 'csv') {
        response = await reportsAPI.exportStudentsCSV();
        filename = 'students_report.csv';
      } else if (type === 'students' && format === 'pdf') {
        response = await reportsAPI.exportStudentsPDF();
        filename = 'students_report.pdf';
      } else if (type === 'lessons' && format === 'csv') {
        response = await reportsAPI.exportLessonsCSV();
        filename = 'lessons_report.csv';
      }
      
      const blob = new Blob([response.data], {
        type: format === 'csv' ? 'text/csv' : 'application/pdf'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${filename} downloaded successfully`);
    } catch (error) {
      toast.error('Failed to download report');
    } finally {
      setDownloading({ ...downloading, [key]: false });
    }
  };

  if (!canEdit()) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
        <p className="text-slate-400">You don't have permission to access reports</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-heading font-bold text-slate-900">Reports</h1>
        <p className="text-slate-500">Export data in CSV and PDF formats</p>
      </div>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Students Report */}
        <Card data-testid="students-report-card">
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <CardTitle>Students Report</CardTitle>
                <CardDescription>Export all student records</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Includes: Name, Age, Program, Location, Status, Enrollment Date
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => handleDownload('students', 'csv')}
                  disabled={downloading['students-csv']}
                  className="flex-1"
                  data-testid="download-students-csv"
                >
                  {downloading['students-csv'] ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                  )}
                  CSV
                </Button>
                <Button
                  onClick={() => handleDownload('students', 'pdf')}
                  disabled={downloading['students-pdf']}
                  className="flex-1 btn-primary"
                  data-testid="download-students-pdf"
                >
                  {downloading['students-pdf'] ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4 mr-2" />
                  )}
                  PDF
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Lessons Report */}
        <Card data-testid="lessons-report-card">
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center text-secondary">
                <Calendar className="w-6 h-6" />
              </div>
              <div>
                <CardTitle>Lessons Report</CardTitle>
                <CardDescription>Export all lesson records</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Includes: Student, Program, Coach, Lesson #, Date, Status, Notes
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => handleDownload('lessons', 'csv')}
                  disabled={downloading['lessons-csv']}
                  className="flex-1"
                  data-testid="download-lessons-csv"
                >
                  {downloading['lessons-csv'] ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                  )}
                  CSV
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center text-slate-400">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-1">About Reports</h3>
              <p className="text-sm text-slate-600">
                Reports are generated in real-time based on current data. CSV files can be opened in Excel or Google Sheets. 
                PDF reports include formatted tables suitable for printing or sharing.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Reports;
